import React, { useMemo } from 'react';
import { BsCaretDownFill, BsCaretRightFill, BsCurrencyDollar, BsCheckCircle, BsExclamationTriangle, BsClock, BsFileEarmarkText, BsPeople } from "react-icons/bs";
import { BillingUtils } from '../Services/billingService';

const BillingKPIGeneral = ({ 
  allActivities = [], // Changed from processedActivities to match the prop name
  selectedActivities = [],
  allActivitiesData = null,
  isFiltered = false,
  totalAvailable = 0,
  onSelectionClear,
  isLoading = false
}) => {
  const [showKPI, setShowKPI] = React.useState(true);

  // Calculate KPIs based on processed activities
  const kpis = useMemo(() => {
    console.log('🔍 [BillingKPIGeneral] Calculating KPIs for activities:', allActivities.length);
    
    // No data case
    if (!allActivities || allActivities.length === 0) {
      return {
        totalActivities: 0,
        totalAmount: 0,
        billedActivities: 0,
        billedAmount: 0,
        notBilledActivities: 0,
        notBilledAmount: 0,
        partiallyBilledActivities: 0,
        partiallyBilledAmount: 0,
        nonBillableActivities: 0,
        selectedCount: 0,
        selectedAmount: 0,
        totalPatients: 0,
        averageAmount: 0,
        billingRate: 0,
        errorRate: 0
      };
    }

    let totalAmount = 0;
    let billedActivities = 0;
    let billedAmount = 0;
    let notBilledActivities = 0;
    let notBilledAmount = 0;
    let partiallyBilledActivities = 0;
    let partiallyBilledAmount = 0;
    let nonBillableActivities = 0;
    let selectedAmount = 0;
    
    // Set to count unique patients
    const uniquePatients = new Set();
    
    // Set of selected activity IDs for calculations
    const selectedIds = new Set(selectedActivities.map(act => 
      typeof act === 'object' ? act.id : act
    ));

    allActivities.forEach(activity => {
      const netAmount = activity.net_amount || 0;
      totalAmount += netAmount;
      
      // Count unique patients
      if (activity.patient_id && activity.patient_id !== 'unknown') {
        uniquePatients.add(activity.patient_id);
      }
      
      // Calculate amount of selected activities
      if (selectedIds.has(activity.id)) {
        selectedAmount += netAmount;
      }
      
      // Classify by status
      const status = activity.status?.toLowerCase() || 'unbilled';
      
      switch (status) {
        case 'billed':
        case 'invoiced':
          billedActivities++;
          billedAmount += activity.billed_net || netAmount;
          break;
          
        case 'partially_billed':
          partiallyBilledActivities++;
          partiallyBilledAmount += activity.remaining_net || netAmount;
          break;
          
        case 'non_billable':
        case 'not_billable':
          nonBillableActivities++;
          break;
          
        case 'unbilled':
        case 'pending':
        case 'draft':
        default:
          notBilledActivities++;
          notBilledAmount += netAmount;
          break;
      }
    });

    const totalPatients = uniquePatients.size;
    const averageAmount = allActivities.length > 0 ? totalAmount / allActivities.length : 0;
    const billingRate = allActivities.length > 0 ? (billedActivities / allActivities.length) * 100 : 0;
    const errorRate = allActivities.length > 0 ? (nonBillableActivities / allActivities.length) * 100 : 0;

    const result = {
      totalActivities: allActivities.length,
      totalAmount,
      billedActivities,
      billedAmount,
      notBilledActivities,
      notBilledAmount,
      partiallyBilledActivities,
      partiallyBilledAmount,
      nonBillableActivities,
      selectedCount: selectedActivities.length,
      selectedAmount,
      totalPatients,
      averageAmount,
      billingRate,
      errorRate
    };
    
    console.log('📊 [BillingKPIGeneral] Calculated KPIs:', result);
    return result;
  }, [allActivities, selectedActivities]);

  // Get global server statistics if available
  const globalStats = useMemo(() => {
    if (!allActivitiesData?.data?.global_summary) {
      return null;
    }
    
    const summary = allActivitiesData.data.global_summary;
    return {
      totalActivities: summary.total_activities || 0,
      totalBillable: summary.total_billable || 0,
      totalAlreadyBilled: summary.total_already_billed || 0,
      totalPatients: summary.total_patients || 0,
      totalAmount: summary.total_amount || 0
    };
  }, [allActivitiesData]);

  // Format number with commas for thousands
  const formatNumber = (value) => {
    if (typeof value === 'number') {
      return value.toLocaleString('en-US');
    }
    return value;
  };

  // Format currency
  const formatCurrency = (value) => {
    return BillingUtils.formatCurrency ? BillingUtils.formatCurrency(value) : `$${(value || 0).toFixed(2)}`;
  };

  // Format percentage
  const formatPercentage = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  // Toggle KPI visibility
  const toggleKPI = () => {
    setShowKPI(!showKPI);
  };

  // Handler to clear selection
  const handleClearSelection = () => {
    if (onSelectionClear) {
      onSelectionClear();
    }
  };

  // Define KPI cards
  const kpiCards = [
    { 
      title: 'Total', 
      value: globalStats?.totalActivities || kpis.totalActivities,
      subtitle: formatCurrency(kpis.totalAmount),
      icon: <BsFileEarmarkText />, 
      iconColor: '#f5f2e8'
    },
    { 
      title: 'Unbilled', 
      value: kpis.notBilledActivities,
      subtitle: formatCurrency(kpis.notBilledAmount),
      icon: <BsClock />, 
      iconColor: '#e8f4fd'
    },
    { 
      title: 'Billed', 
      value: kpis.billedActivities,
      subtitle: formatCurrency(kpis.billedAmount),
      icon: <BsCheckCircle />, 
      iconColor: '#fde8f4'
    },
    { 
      title: 'Partial', 
      value: kpis.partiallyBilledActivities,
      subtitle: formatCurrency(kpis.partiallyBilledAmount),
      icon: <BsExclamationTriangle />, 
      iconColor: '#e8f5e8'
    },    
    { 
      title: 'Selected', 
      value: kpis.selectedCount,
      subtitle: formatCurrency(kpis.selectedAmount),
      icon: <BsCurrencyDollar />, 
      iconColor: '#fff4e8'
    },
    { 
      title: 'Patients', 
      value: globalStats?.totalPatients || kpis.totalPatients,
      icon: <BsPeople />, 
      iconColor: '#f0e8fd'
    }
  ];

  // Show skeleton while loading
  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center">
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      
      
      {showKPI && (
        <div className="row gy-3 introbx_row">
          {kpiCards.map((kpi, index) => (
            <div key={index} className="col">
              <div className="d-flex align-items-center gap-2 p-3 rounded-10 shadow-sm">
                <div className="icon flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle" style={{ width: '2rem', height: '2rem', backgroundColor: '#E4E4FF', color: '#8381FF' }}>
                  <span className="d-block material-symbols-outlined fs-lg">{kpi.icon}</span>
                </div>
                <div className="info flex-fill">
                  <p className="fs-sm text-gray mb-0">{kpi.title}</p>
                  <p className="lh-1 fw-semibold">{formatNumber(kpi.value)}</p>
                  {/* {kpi.subtitle && (
                    <p className="fs-xs text-muted mb-0">{kpi.subtitle}</p>
                  )} */}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default BillingKPIGeneral;